import { VehicleMatch } from '@ltvco/refresh-lib/api';
import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import {
  CheckCircleOutline,
  ErrorOutlineOutlined,
  Star,
} from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';

import { formatPriceRange } from '../../utils';

import { StyledImage } from './components';

export interface IVehicleMatchmakerResultCardProps {
  disableButton: boolean;
  match: VehicleMatch;
  showTopMatchTag: boolean;
  onDetailsClick: (match: VehicleMatch, index: number) => void;
  index: number;
  openReportMatchKey?: string | null;
}

export const VehicleMatchmakerResultCard = ({
  disableButton,
  match,
  showTopMatchTag,
  onDetailsClick,
  index,
  openReportMatchKey,
}: IVehicleMatchmakerResultCardProps) => {
  const theme = useTheme();
  const {
    images: { imgNoVehicleImageProvided },
  } = useAppConstantsContext();
  const [imgError, setImgError] = useState(false);
  const isReportOpenForThisCard = useMemo(
    () =>
      openReportMatchKey != null &&
      openReportMatchKey ===
        `${match.year}-${match.make}-${match.model}-${match.trim ?? 'Base'}`,
    [openReportMatchKey, match],
  );
  const isDetailsDisabled = disableButton || isReportOpenForThisCard;
  const showMpg =
    match.city_mpg != null &&
    match.highway_mpg != null &&
    match.city_mpg > 0 &&
    match.highway_mpg > 0;

  const imageUrl = match.image_url
    ? match.image_url.replace(
        'commons.wikimedia.org/wiki/File:',
        'commons.wikimedia.org/wiki/Special:FilePath/',
      )
    : '';

  const handleImageError = () => {
    setImgError(true);
  };

  const handleDetailsClick = useCallback(() => {
    onDetailsClick(match, index);
  }, [onDetailsClick, match, index]);

  return (
    <Card
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            {Boolean(imageUrl) && !imgError ? (
              <StyledImage
                src={imageUrl}
                alt={`${match.year} ${match.make} ${match.model} ${match.trim || 'Base'}`}
                onError={handleImageError}
              />
            ) : (
              <StyledImage
                src={imgNoVehicleImageProvided}
                alt="No vehicle placeholder image"
                objectFit="cover"
              />
            )}
            <Stack spacing={0.5} flex={1}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={1}
              >
                <Text variant="h5" mb={0}>
                  {match.year} {match.make} {match.model} {match.trim || 'Base'}
                </Text>
                {showTopMatchTag && (
                  <Chip
                    label="Top Match"
                    size="small"
                    icon={<Star sx={{ color: 'inherit !important' }} />}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
              {showMpg && (
                <Text variant="body2" color="text.secondary" py={0.75}>
                  {match.city_mpg}/{match.highway_mpg} mpg
                </Text>
              )}
              <Text variant="body2" mt={0.5}>
                {formatPriceRange(match.price_range_min, match.price_range_max)}
              </Text>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              sx={{
                alignSelf: 'flex-start',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
              disabled={isDetailsDisabled}
              onClick={handleDetailsClick}
            >
              Details
            </Button>
          </Stack>
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              alignItems="flex-start"
              justifyContent={{ xs: 'space-between', sm: 'flex-start' }}
            >
              <Stack
                direction="row"
                spacing={{ xs: 0, sm: 1 }}
                flexWrap="wrap"
                sx={{ flex: 1, minWidth: 0 }}
              >
                {match.pros.slice(0, 3).map((pro: string, idx: number) => (
                  <Text
                    key={`pro-${idx}`}
                    display="flex"
                    variant="body2"
                    alignItems="center"
                    color="text.secondary"
                  >
                    <CheckCircleOutline
                      fontSize="small"
                      sx={{ color: theme.palette.success.dark, mr: 0.5 }}
                    />
                    {pro}
                  </Text>
                ))}
                <Text
                  display="flex"
                  variant="body2"
                  alignItems="center"
                  color="text.secondary"
                >
                  <ErrorOutlineOutlined
                    fontSize="small"
                    sx={{ color: theme.palette.error.main, mr: 0.5 }}
                  />
                  {match.con}
                </Text>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                  display: { xs: 'inline-flex', sm: 'none' },
                }}
                disabled={isDetailsDisabled}
                onClick={handleDetailsClick}
              >
                Details
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};
